/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import {
  ListOrganizationRemoteSessionsResult,
  ListOrganizationRemoteSessionsResult$inboundSchema,
} from "../components/listorganizationremotesessionsresult.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type ListOrganizationRemoteSessionClientSessionsSecurity = {
  sessionHeaderGramSession?: string | undefined;
  apikeyHeaderGramKey?: string | undefined;
};

/**
 * Filter by effective upstream authorization status. Access expiry alone does not expire a refreshable account.
 */
export const QueryParamStatus = {
  Active: "active",
  Expired: "expired",
  All: "all",
} as const;
/**
 * Filter by effective upstream authorization status. Access expiry alone does not expire a refreshable account.
 */
export type QueryParamStatus = ClosedEnum<typeof QueryParamStatus>;

/**
 * Ordering. Authorization expiry is the earliest hard authorization deadline and usable refresh/access deadline. Omit to retain ID ordering and UUID cursors.
 */
export const QueryParamSort = {
  Newest: "newest",
  Oldest: "oldest",
  LastUsed: "last_used",
  Expiry: "expiry",
} as const;
/**
 * Ordering. Authorization expiry is the earliest hard authorization deadline and usable refresh/access deadline. Omit to retain ID ordering and UUID cursors.
 */
export type QueryParamSort = ClosedEnum<typeof QueryParamSort>;

export type ListOrganizationRemoteSessionClientSessionsRequest = {
  /**
   * The remote_session_client id.
   */
  clientId: string;
  /**
   * Case-insensitive literal identity search.
   */
  search?: string | undefined;
  /**
   * Filter by effective upstream authorization status. Access expiry alone does not expire a refreshable account.
   */
  status?: QueryParamStatus | undefined;
  /**
   * Inclusive creation lower bound.
   */
  createdAfter?: Date | undefined;
  /**
   * Exclusive creation upper bound.
   */
  createdBefore?: Date | undefined;
  /**
   * Ordering. Authorization expiry is the earliest hard authorization deadline and usable refresh/access deadline. Omit to retain ID ordering and UUID cursors.
   */
  sort?: QueryParamSort | undefined;
  /**
   * Pagination cursor. Preserve the filters and ordering when following it.
   */
  cursor?: string | undefined;
  /**
   * Page size (default 50, max 100).
   */
  limit?: number | undefined;
  /**
   * Session header
   */
  gramSession?: string | undefined;
  /**
   * API Key header
   */
  gramKey?: string | undefined;
};

export type ListOrganizationRemoteSessionClientSessionsResponse = {
  result: ListOrganizationRemoteSessionsResult;
};

/** @internal */
export type ListOrganizationRemoteSessionClientSessionsSecurity$Outbound = {
  "session_header_Gram-Session"?: string | undefined;
  "apikey_header_Gram-Key"?: string | undefined;
};

/** @internal */
export const ListOrganizationRemoteSessionClientSessionsSecurity$outboundSchema:
  z.ZodMiniType<
    ListOrganizationRemoteSessionClientSessionsSecurity$Outbound,
    ListOrganizationRemoteSessionClientSessionsSecurity
  > = z.pipe(
    z.object({
      sessionHeaderGramSession: z.optional(z.string()),
      apikeyHeaderGramKey: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        sessionHeaderGramSession: "session_header_Gram-Session",
        apikeyHeaderGramKey: "apikey_header_Gram-Key",
      });
    }),
  );

export function listOrganizationRemoteSessionClientSessionsSecurityToJSON(
  listOrganizationRemoteSessionClientSessionsSecurity:
    ListOrganizationRemoteSessionClientSessionsSecurity,
): string {
  return JSON.stringify(
    ListOrganizationRemoteSessionClientSessionsSecurity$outboundSchema.parse(
      listOrganizationRemoteSessionClientSessionsSecurity,
    ),
  );
}

/** @internal */
export const QueryParamStatus$outboundSchema: z.ZodMiniEnum<
  typeof QueryParamStatus
> = z.enum(QueryParamStatus);

/** @internal */
export const QueryParamSort$outboundSchema: z.ZodMiniEnum<
  typeof QueryParamSort
> = z.enum(QueryParamSort);

/** @internal */
export type ListOrganizationRemoteSessionClientSessionsRequest$Outbound = {
  client_id: string;
  search?: string | undefined;
  status?: string | undefined;
  created_after?: string | undefined;
  created_before?: string | undefined;
  sort?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
  "Gram-Session"?: string | undefined;
  "Gram-Key"?: string | undefined;
};

/** @internal */
export const ListOrganizationRemoteSessionClientSessionsRequest$outboundSchema:
  z.ZodMiniType<
    ListOrganizationRemoteSessionClientSessionsRequest$Outbound,
    ListOrganizationRemoteSessionClientSessionsRequest
  > = z.pipe(
    z.object({
      clientId: z.string(),
      search: z.optional(z.string()),
      status: z.optional(QueryParamStatus$outboundSchema),
      createdAfter: z.optional(
        z.pipe(z.date(), z.transform(v => v.toISOString())),
      ),
      createdBefore: z.optional(
        z.pipe(z.date(), z.transform(v => v.toISOString())),
      ),
      sort: z.optional(QueryParamSort$outboundSchema),
      cursor: z.optional(z.string()),
      limit: z.optional(z.int()),
      gramSession: z.optional(z.string()),
      gramKey: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        clientId: "client_id",
        createdAfter: "created_after",
        createdBefore: "created_before",
        gramSession: "Gram-Session",
        gramKey: "Gram-Key",
      });
    }),
  );

export function listOrganizationRemoteSessionClientSessionsRequestToJSON(
  listOrganizationRemoteSessionClientSessionsRequest:
    ListOrganizationRemoteSessionClientSessionsRequest,
): string {
  return JSON.stringify(
    ListOrganizationRemoteSessionClientSessionsRequest$outboundSchema.parse(
      listOrganizationRemoteSessionClientSessionsRequest,
    ),
  );
}

/** @internal */
export const ListOrganizationRemoteSessionClientSessionsResponse$inboundSchema:
  z.ZodMiniType<ListOrganizationRemoteSessionClientSessionsResponse, unknown> =
    z.pipe(
      z.object({
        Result: ListOrganizationRemoteSessionsResult$inboundSchema,
      }),
      z.transform((v) => {
        return remap$(v, {
          "Result": "result",
        });
      }),
    );

export function listOrganizationRemoteSessionClientSessionsResponseFromJSON(
  jsonString: string,
): SafeParseResult<
  ListOrganizationRemoteSessionClientSessionsResponse,
  SDKValidationError
> {
  return safeParse(
    jsonString,
    (x) =>
      ListOrganizationRemoteSessionClientSessionsResponse$inboundSchema.parse(
        JSON.parse(x),
      ),
    `Failed to parse 'ListOrganizationRemoteSessionClientSessionsResponse' from JSON`,
  );
}
